import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback, useContext } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';



//import { _myAccount } from '_mock';
import { AuthContext } from 'components/authentication/context/authContext';
import { getUserEntity } from 'common/UserEntityProvider';

import intl from 'components/i18n/ReactIntlWrapper';
import { PeopleAlt } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router';

// ----------------------------------------------------------------------

export type AccountPopoverProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
    viewScopes?: string[];
    editScopes?: string[];
    viewRoles?: string[];
    editRoles?: string[];
    entityType?: number;
  }[];
};

export function AccountPopover({ data = [], sx, ...other }: AccountPopoverProps) {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  const handleClickItem = useCallback(
    (path: string) => {
      handleClosePopover();
      navigate(path);
    },
    [handleClosePopover, navigate]
  );

  const authContext = useContext(AuthContext);
  const { switchUserEntity, users } = useContext(AuthContext);
  const userDetails = getUserEntity();

  const handleLogout = async () => {
    authContext.signOut();
  }

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          p: '2px',
          ml: 2,
          width: 40,
          height: 40,
          background: (theme) =>
            `conic-gradient(${theme.vars.palette.primary.light}, ${theme.vars.palette.warning.light}, ${theme.vars.palette.primary.light})`,
          ...sx,
        }}
        {...other}
      >
        <Avatar sx={{ width: 1, height: 1 }}>
          {userDetails?.entityName?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 250 },
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {userDetails?.entityName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {userDetails?.email}
          </Typography>
        </Box>

        <Divider />

        <MenuList
          disablePadding
          sx={{
            py: 1,
            gap: 0.5,
            display: 'flex',
            flexDirection: 'column',
            [`& .${menuItemClasses.root}`]: {
              px: 1,
              gap: 2,
              borderRadius: 0.75,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              [`&.${menuItemClasses.selected}`]: {
                color: 'text.primary',
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightSemiBold',
              },
            },
          }}
        >
          <Typography variant="h6" sx={{ px: 1.5 }} display={'flex'} alignItems='center' gap={1}>
            <PeopleAlt />{intl.get('profileContent.userAccounts')}
          </Typography>
          {users.length != 0 && users?.filter((user) => user.id != userDetails?.id).map((option) => (
            <MenuItem
              key={`${option.id}-option`}
              onClick={() => switchUserEntity(option.id)}
              sx={{mx:1}}
            >
              {option.entityName}
            </MenuItem>
          ))}
          <Divider />
          {data.map((option) => (
            <MenuItem
              key={option.label}
              title={option.label} 
              selected={option.href === pathname}
              onClick={() => handleClickItem(option.href)}
              sx={{mx:1}}
            >
              {option.icon}
              <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                {option.label}
              </span>
            </MenuItem>
          ))}
        </MenuList>

        <Divider />

        <Box sx={{ p: .5 }}>
          <Button fullWidth color="error" size="small" variant="text" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Popover>
    </>
  );
}
